import { TableMetadata, TableColumnData } from "@king-ict/eupisi-ui/components";
import { hrDateWithTime } from "@king-ict/eupisi-ui/utils";

import { Role } from "../../../models";
import {
  CommitteeTypeEnum,
  CommitteeEmployee,
} from "../../../models/Committee";
import {
  committeeDecisionDetailsUrl,
  committeeDecisionEditUrl,
} from "../../../routes/routes";
import { RuleUtils, FormatUtils } from "../../../utils";
import { GetTableMetadataProps } from "./types";
import {
  findDecisionForCommittee,
  renderActionButton,
  renderEnrollmentDecision,
  hasDecisionPermission,
} from "./utils";

export const ALLOWED_ROLES_BASE_FOR_EDITING_DECISION = [
  Role.CAS,
  Role.Helpdesk,
];
export const ALLOWED_ROLES_INSTITUTION_FOR_EDITING_DECISION = [
  Role.InstitutionPrincipal,
  Role.InstitutionAdministrator,
];
export const ALLOWED_ROLES_COUNTY_FOR_EDITING_DECISION = [
  Role.CountyAdministrator,
  Role.CountyLeader,
];

export const getInstitutionCommitteeTableMetadata = ({
  t,
  committeeDecisions,
  item,
  history,
  auth,
  committeeType,
  committees,
}: GetTableMetadataProps): TableMetadata<CommitteeEmployee> => {
  const handleCommitteeDecisionDetailsClicked = (
    committeeDecisionId: number,
    committeeEmployeeId: number
  ) => {
    history.push(
      committeeDecisionDetailsUrl(
        committeeDecisionId,
        item.id,
        committeeEmployeeId
      )
    );
  };

  const handleCommitteeDecisionNewClicked = (committeeEmployeeId: number) => {
    history.push(committeeDecisionEditUrl(0, item.id, committeeEmployeeId));
  };

  const renderDecisionCell = (committeeEmployee: CommitteeEmployee) => {
    if (!committeeEmployee.employee) return null;

    const decision = findDecisionForCommittee(
      committeeEmployee,
      committeeDecisions
    );

    // If there is already a decision, show details button so they can VIEW
    if (decision) {
      return renderActionButton(t("common.details"), () =>
        handleCommitteeDecisionDetailsClicked(
          decision.id,
          decision.committeeEmployeeId
        )
      );
    }

    const isStatusEditable = RuleUtils.canEditCommitteeDecision(
      item,
      committeeType
    );

    const canCreateNewOpinion = hasDecisionPermission({
      committeeEmployee,
      auth,
      committeeType,
      committees,
      // onlyPresident: true, // FIXME: There is a case where we will need to check if the user is president only! But for now we are just letting it go as is
    });

    // Show details if there is no decision and they will be thrown into creating a new one
    if (isStatusEditable && canCreateNewOpinion) {
      return renderActionButton(t("common.details"), () =>
        handleCommitteeDecisionNewClicked(committeeEmployee.id)
      );
    }

    return null;
  };

  const roleColumn = {
    headerClassName: "col-2",
    columnKey: "expertCommissionPosition",
    header: t("common.expertCommission.position"),
    accessor: (item: CommitteeEmployee) => {
      return <div>{item.committeePosition?.name}</div>;
    },
  };

  const columns = [
    {
      headerClassName: "col-3",
      columnKey: "expertCommissionRole",
      header: t("common.expertCommission.role"),
      accessor: ({ committeeRole }: CommitteeEmployee) => (
        <div>{committeeRole.name}</div>
      ),
    },
    ...(committeeType === CommitteeTypeEnum.County ? [roleColumn] : []),
    {
      headerClassName: "col-3",
      columnKey: "employee",
      header: t("common.expertCommission.employee"),
      accessor: ({ employee }: CommitteeEmployee) => (
        <div>{employee?.name}</div>
      ),
    },
    {
      headerClassName: "col-2",
      columnKey: "activeTo",
      header: t("common.expertCommission.activeTo"),
      accessor: ({ deactivationDate }: CommitteeEmployee) => {
        return <div>{FormatUtils.formatDate(deactivationDate)}</div>;
      },
    },
    {
      headerClassName: "col-2",
      columnKey: "updatedDate",
      header: t("committeeDecision.updatedDate"),
      accessor: (committee: CommitteeEmployee) => {
        const decision = findDecisionForCommittee(
          committee,
          committeeDecisions
        );

        return (
          <div>
            {decision?.dateCreated ? hrDateWithTime(decision.dateCreated) : ""}
          </div>
        );
      },
    },
    {
      headerClassName: "col-2",
      columnKey: "committeeEnrollmentDecision",
      header: t("committeeDecision.committeeEnrollmentDecision"),
      accessor: (committee: CommitteeEmployee) => {
        return renderEnrollmentDecision(committee, committeeDecisions);
      },
    },
    {
      columnKey: "details",
      header: "",
      accessor: renderDecisionCell,
    },
  ];

  return {
    rowKey: "rowKeyId",
    columns: columns as TableColumnData<CommitteeEmployee>[],
    rowToolTip: () => undefined,
  };
};
